import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Loader2, ExternalLink, MessageSquareWarning, ImageIcon, Search, Calendar, User, Paperclip, Download, HandCoins, Truck, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_LABELS = {
    cheque_o: 'Chèque O',
    cheque_so: 'Chèque SO',
    espece: 'Espèces',
    virement: 'Virement',
};

// Paiements qui nécessitent une récupération physique par l'admin
const PAIEMENTS_A_RECUPERER = new Set(['cheque_o', 'cheque_so', 'espece']);

// Palette dédiée à chaque commercial (cohérente dans toute l'app).
const COMMERCIAL_COLORS = {
    Axel: 'bg-blue-100 text-blue-800 border-blue-200',
    Melissa: 'bg-pink-100 text-pink-800 border-pink-200',
    Syphax: 'bg-orange-100 text-orange-800 border-orange-200',
    Isaac: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};
const getCommercialClass = (name) =>
    COMMERCIAL_COLORS[name] || 'bg-slate-100 text-slate-700 border-slate-200';

function formatDateTime(iso) {
    if (!iso) return '-';
    try { return format(new Date(iso), 'dd/MM/yyyy', { locale: fr }); } catch { return iso; }
}

function volumeEcart(row) {
    const vd = parseFloat(row.volume_devis);
    const vr = parseFloat(row.volume_reel);
    if (Number.isNaN(vd) || Number.isNaN(vr)) return null;
    return vr - vd;
}

function TableWithTopScroll({ children }) {
    const topRef = useRef(null);
    const containerRef = useRef(null);
    const [scrollWidth, setScrollWidth] = useState(0);
    const [needsScroll, setNeedsScroll] = useState(false);

    // Mesure + observer le scroll width de la table interne
    useLayoutEffect(() => {
        const wrapper = containerRef.current?.querySelector('.relative.w-full.overflow-auto');
        if (!wrapper) return;
        const measure = () => {
            setScrollWidth(wrapper.scrollWidth);
            setNeedsScroll(wrapper.scrollWidth > wrapper.clientWidth + 1);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(wrapper);
        if (wrapper.firstElementChild) ro.observe(wrapper.firstElementChild);
        return () => ro.disconnect();
    });

    // Sync bidirectionnel entre barre du haut et conteneur interne
    useEffect(() => {
        const wrapper = containerRef.current?.querySelector('.relative.w-full.overflow-auto');
        const top = topRef.current;
        if (!wrapper || !top) return;
        let syncing = false;
        const onTop = () => {
            if (syncing) return;
            syncing = true;
            wrapper.scrollLeft = top.scrollLeft;
            requestAnimationFrame(() => { syncing = false; });
        };
        const onBottom = () => {
            if (syncing) return;
            syncing = true;
            top.scrollLeft = wrapper.scrollLeft;
            requestAnimationFrame(() => { syncing = false; });
        };
        top.addEventListener('scroll', onTop, { passive: true });
        wrapper.addEventListener('scroll', onBottom, { passive: true });
        return () => {
            top.removeEventListener('scroll', onTop);
            wrapper.removeEventListener('scroll', onBottom);
        };
    }, [scrollWidth]);

    return (
        <>
            {needsScroll && (
                <div
                    ref={topRef}
                    className="overflow-x-auto border-b border-slate-200"
                    style={{ height: 14 }}
                    data-testid="top-scrollbar"
                >
                    <div style={{ width: scrollWidth, height: 1 }} />
                </div>
            )}
            <div ref={containerRef} className="hide-inner-scrollbar">{children}</div>
        </>
    );
}

/**
 * Barre de filtres par onglet — un seul Select unifié.
 * Définie HORS du composant de page pour garder une identité stable :
 * sinon le champ de recherche est démonté à chaque frappe (perte de focus).
 * `isDemenagement` = true ajoute les options "Écart volume > 5 m³" et "Prestation après 19h".
 */
const FilterBar = ({
    search, setSearch,
    filter, setFilter,
    isDemenagement,
    testidSuffix,
}) => (
    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
        <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        placeholder={isDemenagement
                            ? 'Rechercher (client, commercial, chef…)'
                            : 'Rechercher (client, livreur…)'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid={`search-input-${testidSuffix}`}
                    />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-full sm:w-[260px]" data-testid={`filter-${testidSuffix}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="probleme">Problème signalé</SelectItem>
                        <SelectItem value="paiement_recup">Paiement à récupérer</SelectItem>
                        {isDemenagement && (
                            <>
                                <SelectItem value="volume_err">Écart volume {'>'} 5 m³</SelectItem>
                                <SelectItem value="after_19h">Prestation après 19h</SelectItem>
                            </>
                        )}
                    </SelectContent>
                </Select>
            </div>
        </CardContent>
    </Card>
);

export default function EspaceDemenageursPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('demenagement');

    const PAGE_SIZE = 15;

    // Un filtre unique par onglet : 'all' | 'probleme' | 'volume_err' | 'after_19h'
    const [demSearch, setDemSearch] = useState('');
    const [demFilter, setDemFilter] = useState('all');
    const [demPage, setDemPage] = useState(1);
    const [demData, setDemData] = useState({ items: [], total: 0, totalPages: 1 });
    const [demLoading, setDemLoading] = useState(true);

    const [livSearch, setLivSearch] = useState('');
    const [livFilter, setLivFilter] = useState('all');
    const [livPage, setLivPage] = useState(1);
    const [livData, setLivData] = useState({ items: [], total: 0, totalPages: 1 });
    const [livLoading, setLivLoading] = useState(true);

    // Recherche débouncée : évite une requête à chaque frappe
    const [demSearchDeb, setDemSearchDeb] = useState('');
    const [livSearchDeb, setLivSearchDeb] = useState('');
    useEffect(() => {
        const t = setTimeout(() => { setDemSearchDeb(demSearch); setDemPage(1); }, 350);
        return () => clearTimeout(t);
    }, [demSearch]);
    useEffect(() => {
        const t = setTimeout(() => { setLivSearchDeb(livSearch); setLivPage(1); }, 350);
        return () => clearTimeout(t);
    }, [livSearch]);

    // Changement de filtre -> retour page 1
    useEffect(() => { setDemPage(1); }, [demFilter]);
    useEffect(() => { setLivPage(1); }, [livFilter]);

    // Pagination côté serveur : l'API ne renvoie que les 15 lignes de la page.
    const fetchTab = (type, page, search, filter, setData, setLoading) => {
        setLoading(true);
        const params = { type_prestation: type, page, page_size: PAGE_SIZE };
        if (search.trim()) params.search = search.trim();
        if (filter !== 'all') params.filtre = filter;
        axios.get(`${API}/api/exploitation/espace-demenageurs`, { params, withCredentials: true })
            .then(res => setData({
                items: res.data.items || [],
                total: res.data.total || 0,
                totalPages: res.data.total_pages || 1,
            }))
            .catch(() => setData({ items: [], total: 0, totalPages: 1 }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTab('demenagement', demPage, demSearchDeb, demFilter, setDemData, setDemLoading);
    }, [demPage, demSearchDeb, demFilter]);
    useEffect(() => {
        fetchTab('livraison', livPage, livSearchDeb, livFilter, setLivData, setLivLoading);
    }, [livPage, livSearchDeb, livFilter]);

    // Dialog signalement
    const [problemeOpen, setProblemeOpen] = useState(false);
    const [problemeText, setProblemeText] = useState('');
    const [problemeClient, setProblemeClient] = useState('');

    // Dialog preuve image : contient une liste de fichiers (1 pour le moment, mais extensible)
    const [preuveOpen, setPreuveOpen] = useState(false);
    const [preuveFiles, setPreuveFiles] = useState([]); // [{ url, filename }]
    const [preuveClient, setPreuveClient] = useState('');

    // Dialog confirmation "Récupération paiement"
    const [confirmPayOpen, setConfirmPayOpen] = useState(false);
    const [confirmPayRow, setConfirmPayRow] = useState(null);
    const [confirmPaySubmitting, setConfirmPaySubmitting] = useState(false);

    const openProbleme = (row) => {
        setProblemeText(row.signalement_probleme || '');
        setProblemeClient(row.nom_client || '');
        setProblemeOpen(true);
    };

    const openPreuve = (row) => {
        // Utilise la liste `files` renvoyée par le backend (multi), fallback sur le legacy (1 fichier).
        const list = (row.files && row.files.length)
            ? row.files
            : (row.has_preuve ? [{ filename: row.preuve_filename || 'preuve.jpg' }] : []);
        setPreuveFiles(list.map((f, idx) => ({
            url: `${API}/api/exploitation/espace-demenageurs/${row.id}/preuve/${idx}`,
            filename: f.filename || `preuve-${idx + 1}.jpg`,
        })));
        setPreuveClient(row.nom_client || '');
        setPreuveOpen(true);
    };

    const askConfirmPay = (row) => {
        setConfirmPayRow(row);
        setConfirmPayOpen(true);
    };

    const confirmPay = async () => {
        if (!confirmPayRow) return;
        setConfirmPaySubmitting(true);
        try {
            await axios.post(
                `${API}/api/exploitation/espace-demenageurs/${confirmPayRow.id}/confirm-paiement`,
                {},
                { withCredentials: true },
            );
            setDemData(prev => ({ ...prev, items: prev.items.map(e => e.id === confirmPayRow.id ? { ...e, paiement_recupere: true } : e) }));
            setLivData(prev => ({ ...prev, items: prev.items.map(e => e.id === confirmPayRow.id ? { ...e, paiement_recupere: true } : e) }));
            toast.success('Paiement marqué comme récupéré');
            setConfirmPayOpen(false);
            setConfirmPayRow(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la confirmation');
        } finally {
            setConfirmPaySubmitting(false);
        }
    };

    const formUrl = `${window.location.origin}/espace-demenageurs/form`;

    const renderTable = (rows, type, hasActiveFilter, loading, page, setPage, total, totalPages) => (
        <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <CardContent className="p-0">
                {loading ? (
                    <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-slate-500 text-sm">
                        {hasActiveFilter
                            ? 'Aucune saisie ne correspond aux filtres appliqués.'
                            : `Aucune saisie ${type === 'demenagement' ? 'de déménagement' : 'de livraison'} pour l'instant.`}
                    </div>
                ) : (
                    <TableWithTopScroll>
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50/70">
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Nom client</TableHead>
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Date saisie</TableHead>
                                    {type === 'demenagement' ? (
                                        <>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Fin chantier</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Vol. devis</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Vol. réel</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Écart</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Commercial</TableHead>
                                            <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Chef d'équipe</TableHead>
                                        </>
                                    ) : (
                                        <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Livreur</TableHead>
                                    )}
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold text-right whitespace-nowrap">Encaissé</TableHead>
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Mode</TableHead>
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Problème</TableHead>
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap">Fichier</TableHead>
                                    <TableHead className="px-5 text-xs uppercase text-slate-500 font-semibold whitespace-nowrap text-right">Paiement</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="select-none">
                                {rows.map((r) => {
                                    const ecart = volumeEcart(r);
                                    const ecartOver = ecart !== null && Math.abs(ecart) > 5;
                                    const aRecuperer = PAIEMENTS_A_RECUPERER.has(r.mode_paiement) && !r.paiement_recupere;
                                    return (
                                        <TableRow
                                            key={r.id}
                                            className={`transition-colors ${aRecuperer ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}
                                            data-testid={`row-${r.id}`}
                                        >
                                            <TableCell className="px-5 font-medium text-slate-900">
                                                <span className="flex items-center gap-2 max-w-[280px]">
                                                    {type === 'demenagement'
                                                        ? (
                                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-[#2e7cd6] shrink-0" title="Déménagement">
                                                                <Truck size={14} />
                                                            </span>
                                                        )
                                                        : (
                                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 text-amber-500 shrink-0" title="Livraison">
                                                                <Package size={14} />
                                                            </span>
                                                        )}
                                                    <span className="min-w-0 truncate whitespace-nowrap" title={r.nom_client || ''}>{r.nom_client || '-'}</span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="px-5 text-slate-600 text-xs whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Calendar size={13} className="text-slate-400" />
                                                    {formatDateTime(r.created_at)}
                                                </span>
                                            </TableCell>
                                            {type === 'demenagement' ? (
                                                <>
                                                    <TableCell className="px-5 text-slate-600 whitespace-nowrap">{r.heure_fin_chantier || '-'}</TableCell>
                                                    <TableCell className="px-5 text-slate-600 whitespace-nowrap">{r.volume_devis || '-'}</TableCell>
                                                    <TableCell className="px-5 text-slate-600 whitespace-nowrap">{r.volume_reel || '-'}</TableCell>
                                                    <TableCell className="px-5 whitespace-nowrap">
                                                        {ecart === null
                                                            ? <span className="text-slate-400 text-xs">-</span>
                                                            : (
                                                                <span className={`text-xs font-medium ${ecartOver ? 'text-red-600' : 'text-slate-600'}`}>
                                                                    {ecart > 0 ? '+' : ''}{ecart.toFixed(1)} m³
                                                                </span>
                                                            )}
                                                    </TableCell>
                                                    <TableCell className="px-5 whitespace-nowrap">
                                                        {r.commercial
                                                            ? <Badge variant="outline" className={`text-xs font-medium border ${getCommercialClass(r.commercial)}`}>{r.commercial}</Badge>
                                                            : <span className="text-slate-400 text-xs">-</span>}
                                                    </TableCell>
                                                    <TableCell className="px-5 text-slate-600">
                                                        <span className="block max-w-[200px] truncate whitespace-nowrap" title={r.chef_equipe || ''}>{r.chef_equipe || '-'}</span>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <TableCell className="px-5 text-slate-600">
                                                    <span className="block max-w-[200px] truncate whitespace-nowrap" title={r.livreur || ''}>{r.livreur || '-'}</span>
                                                </TableCell>
                                            )}
                                            <TableCell className="px-5 text-right text-slate-900 font-medium whitespace-nowrap">
                                                {r.montant_encaisse ? `${Number(r.montant_encaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}
                                            </TableCell>
                                            <TableCell className="px-5 whitespace-nowrap">
                                                {r.client_non_regle
                                                    ? <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200" data-testid={`non-regle-badge-${r.id}`}>Le client n'a pas payé</Badge>
                                                    : r.mode_paiement
                                                        ? <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{MODE_LABELS[r.mode_paiement] || r.mode_paiement}</Badge>
                                                        : <span className="text-slate-400 text-xs">-</span>}
                                            </TableCell>
                                            <TableCell className="px-5 whitespace-nowrap">
                                                {r.signalement_probleme
                                                    ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openProbleme(r)}
                                                            className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 hover:underline text-sm font-medium"
                                                            data-testid={`probleme-oui-${r.id}`}
                                                        >
                                                            <MessageSquareWarning size={14} /> Oui
                                                        </button>
                                                    )
                                                    : <span className="text-slate-400 text-xs">-</span>}
                                            </TableCell>
                                            <TableCell className="px-5 whitespace-nowrap">
                                                {r.has_preuve
                                                    ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openPreuve(r)}
                                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-[#2e7cd6] text-slate-600 hover:text-[#2e7cd6] text-xs font-medium transition-all"
                                                            data-testid={`preuve-thumb-${r.id}`}
                                                            title={r.preuve_filename || 'Voir les fichiers'}
                                                        >
                                                            <Paperclip size={13} />
                                                            {r.files_count || 1} fichier{(r.files_count || 1) > 1 ? 's' : ''}
                                                        </button>
                                                    )
                                                    : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><ImageIcon size={14} /> -</span>}
                                            </TableCell>
                                            <TableCell className="px-5 whitespace-nowrap text-right">
                                                {PAIEMENTS_A_RECUPERER.has(r.mode_paiement)
                                                    ? (r.paiement_recupere
                                                        ? <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Récupéré</Badge>
                                                        : (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => askConfirmPay(r)}
                                                                className="h-8 text-xs bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                                                                data-testid={`recup-pay-btn-${r.id}`}
                                                            >
                                                                <HandCoins size={14} className="mr-1" />
                                                                Récupération paiement
                                                            </Button>
                                                        )
                                                    )
                                                    : <span className="text-slate-400 text-xs">-</span>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableWithTopScroll>
                )}
                {!loading && total > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 border-t border-slate-200" data-testid={`pagination-${type}`}>
                        <span className="text-xs text-slate-500">
                            {total} saisie{total > 1 ? 's' : ''} — page {page} sur {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                                data-testid={`pagination-prev-${type}`}
                            >
                                <ChevronLeft size={14} className="mr-1" /> Précédent
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                                data-testid={`pagination-next-${type}`}
                            >
                                Suivant <ChevronRight size={14} className="ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    const demHasFilter = demSearch || demFilter !== 'all';
    const livHasFilter = livSearch || livFilter !== 'all';

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="espace-demenageurs-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/exploitation')}
                        data-testid="back-btn"
                        className="text-slate-700 border-slate-300 hover:bg-slate-100"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Espace déménageurs
                        </h1>
                        <p className="mt-1 text-slate-500 text-sm">
                            Saisies effectuées sur le terrain via le formulaire public.
                        </p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="text-[#2e7cd6] border-[#2e7cd6] hover:bg-blue-50"
                    onClick={() => { navigator.clipboard.writeText(formUrl); toast.success('Lien du formulaire copié'); }}
                    data-testid="copy-form-link-btn"
                >
                    <ExternalLink size={16} className="mr-2" /> Copier le lien du formulaire
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4">
                    <TabsTrigger
                        value="demenagement"
                        className="data-[state=active]:bg-blue-100 data-[state=active]:text-[#2e7cd6] data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                        data-testid="tab-demenagement"
                    >
                        Déménagement ({demData.total})
                    </TabsTrigger>
                    <TabsTrigger
                        value="livraison"
                        className="data-[state=active]:bg-blue-100 data-[state=active]:text-[#2e7cd6] data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                        data-testid="tab-livraison"
                    >
                        Livraison ({livData.total})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="demenagement">
                    <FilterBar
                        search={demSearch} setSearch={setDemSearch}
                        filter={demFilter} setFilter={setDemFilter}
                        isDemenagement
                        testidSuffix="dem"
                    />
                    {renderTable(demData.items, 'demenagement', demHasFilter, demLoading, demPage, setDemPage, demData.total, demData.totalPages)}
                </TabsContent>

                <TabsContent value="livraison">
                    <FilterBar
                        search={livSearch} setSearch={setLivSearch}
                        filter={livFilter} setFilter={setLivFilter}
                        isDemenagement={false}
                        testidSuffix="liv"
                    />
                    {renderTable(livData.items, 'livraison', livHasFilter, livLoading, livPage, setLivPage, livData.total, livData.totalPages)}
                </TabsContent>
            </Tabs>

            {/* Dialog Signalement */}
            <Dialog open={problemeOpen} onOpenChange={setProblemeOpen}>
                <DialogContent className="max-w-md" data-testid="probleme-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <MessageSquareWarning size={18} />
                            Problème signalé {problemeClient && <span className="text-slate-600 font-normal">— {problemeClient}</span>}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap bg-amber-50 border border-amber-100 rounded-md p-3">
                        {problemeText || '-'}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Preuve(s) : affiche la liste complète des fichiers joints */}
            <Dialog open={preuveOpen} onOpenChange={setPreuveOpen}>
                <DialogContent className="max-w-2xl" data-testid="preuve-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Paperclip size={18} className="text-[#2e7cd6]" />
                            Fichiers joints {preuveClient && <span className="text-slate-600 font-normal">— {preuveClient}</span>}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {preuveFiles.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-6">Aucun fichier joint.</p>
                        ) : preuveFiles.map((f, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={`preuve-item-${idx}`}>
                                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Paperclip size={14} className="text-slate-500 shrink-0" />
                                        <span className="text-sm text-slate-700 truncate" title={f.filename}>{f.filename}</span>
                                    </div>
                                    <a
                                        href={f.url}
                                        download={f.filename}
                                        className="inline-flex items-center gap-1 text-xs text-[#2e7cd6] hover:text-[#2568b5] font-medium shrink-0"
                                        data-testid={`preuve-download-${idx}`}
                                    >
                                        <Download size={13} /> Télécharger
                                    </a>
                                </div>
                                <div className="flex justify-center bg-slate-50 p-3">
                                    <img src={f.url} alt={f.filename} className="max-h-[60vh] rounded object-contain" />
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog Confirmation récupération paiement */}
            <Dialog open={confirmPayOpen} onOpenChange={setConfirmPayOpen}>
                <DialogContent className="max-w-md" data-testid="confirm-pay-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <HandCoins size={18} className="text-[#2e7cd6]" />
                            Confirmer la récupération du paiement
                        </DialogTitle>
                    </DialogHeader>
                    {confirmPayRow && (
                        <div className="text-sm text-slate-700 space-y-2">
                            <p>
                                Confirmez-vous avoir récupéré le paiement de{' '}
                                <span className="font-semibold text-slate-900">{confirmPayRow.nom_client}</span> ?
                            </p>
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 space-y-1">
                                <div><span className="text-slate-500">Montant :</span> <span className="font-semibold text-slate-900">{confirmPayRow.montant_encaisse ? `${Number(confirmPayRow.montant_encaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}</span></div>
                                <div><span className="text-slate-500">Mode :</span> <span className="font-semibold text-slate-900">{MODE_LABELS[confirmPayRow.mode_paiement] || confirmPayRow.mode_paiement}</span></div>
                            </div>
                            <p className="text-xs text-slate-500">
                                Une fois confirmé, la ligne ne sera plus signalée comme « à récupérer ».
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmPayOpen(false)}
                            disabled={confirmPaySubmitting}
                            data-testid="confirm-pay-cancel"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={confirmPay}
                            disabled={confirmPaySubmitting}
                            className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                            data-testid="confirm-pay-validate"
                        >
                            {confirmPaySubmitting
                                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Enregistrement…</>
                                : 'Confirmer la récupération'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
